import { formatDistance } from "date-fns";
import { GitCommitHorizontal, UserPen } from "lucide-react";
import { useUserDisplayNames } from "@/store/teamsStore";

interface SopSyncBannerProps {
  sopVersion: string | null;
  sopUpdatedBy: string | null;
  updatedAt: Date;
}

/**
 * Compact inline status indicator for the SOP-synced fields section.
 * The org is treated as "covered by sync" iff sopVersion is non-null
 * (sync leaves a SHA behind that survives later user edits).
 */
export function SopSyncBanner({
  sopVersion,
  sopUpdatedBy,
  updatedAt,
}: SopSyncBannerProps) {
  const userNames = useUserDisplayNames();
  const relativeTime = formatDistance(new Date(updatedAt), new Date(), {
    addSuffix: true,
  });

  if (sopUpdatedBy === "sync" && sopVersion) {
    const shortSha = sopVersion.slice(0, 7);
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <GitCommitHorizontal className="h-3.5 w-3.5" />
        <span>
          Configured · <span className="font-mono">{shortSha}</span> ·{" "}
          {relativeTime}
        </span>
      </div>
    );
  }

  if (sopUpdatedBy && sopUpdatedBy !== "sync") {
    const userName = userNames.get(sopUpdatedBy) ?? "an admin";
    const isCovered = sopVersion !== null;
    if (isCovered) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-warning-foreground">
          <UserPen className="h-3.5 w-3.5" />
          <span>
            Manually edited by {userName} · {relativeTime} · will revert on next
            system update
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <UserPen className="h-3.5 w-3.5" />
        <span>
          Edited by {userName} · {relativeTime}
        </span>
      </div>
    );
  }

  return (
    <div className="text-xs text-muted-foreground">Not yet configured</div>
  );
}
